import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import './AdminCommon.css';

const TRANSPORTS = [
  { value: 'http', label: 'HTTP (JSON-RPC)' },
  { value: 'sse', label: 'SSE (Server-Sent Events)' },
  { value: 'stdio', label: 'stdio (local subprocess)' },
];

/**
 * McpServersPage -- list / create / edit / delete / test workspace MCP servers.
 *
 * MCP servers are workspace-scoped registry entries that the Python
 * process-map node's bundled cm_mcp helper can call. Credentials are
 * referenced by name from WorkspaceSecret so they never appear in the
 * registry rows themselves.
 */
function McpServersPage() {
  const [servers, setServers] = useState([]);
  const [secrets, setSecrets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [testResults, setTestResults] = useState({});

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      apiService.listMcpServers().catch(() => ({ servers: [] })),
      apiService.listWorkspaceSecrets().catch(() => ({ secrets: [] })),
    ])
      .then(([srv, sec]) => {
        if (cancelled) return;
        setServers(Array.isArray(srv?.servers) ? srv.servers : []);
        setSecrets(Array.isArray(sec?.secrets) ? sec.secrets : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.error || err?.message || 'Failed to load MCP servers');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const blankForm = {
    name: '',
    description: '',
    transport: 'http',
    url: '',
    command: '',
    args: '',
    env: '',
    auth_header_name: '',
    auth_credential_ref: '',
    enabled: true,
    scope: 'workspace',
  };
  const [form, setForm] = useState(blankForm);

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm);
    setSubmitError(null);
    setShowForm(true);
  };

  const openEdit = (server) => {
    setEditing(server);
    setForm({
      name: server.name,
      description: server.description || '',
      transport: server.transport,
      url: server.url || '',
      command: server.command || '',
      args: (server.args || []).join('\n'),
      env: Object.entries(server.env || {}).map(([k, v]) => `${k}=${v}`).join('\n'),
      auth_header_name: server.auth_header_name || '',
      auth_credential_ref: server.auth_credential_ref || '',
      enabled: server.enabled !== false,
      scope: server.workspace_id ? 'workspace' : 'global',
    });
    setSubmitError(null);
    setShowForm(true);
  };

  const parseArgs = (text) => text.split('\n').map((s) => s.trim()).filter(Boolean);
  const parseEnv = (text) => {
    const out = {};
    text.split('\n').forEach((line) => {
      const idx = line.indexOf('=');
      if (idx > 0) {
        out[line.slice(0, idx).trim()] = line.slice(idx + 1);
      }
    });
    return out;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        transport: form.transport,
        url: form.url || null,
        command: form.command || null,
        args: parseArgs(form.args),
        env: parseEnv(form.env),
        auth_header_name: form.auth_header_name || null,
        auth_credential_ref: form.auth_credential_ref || null,
        enabled: !!form.enabled,
      };
      if (!editing) {
        payload.scope = form.scope;
        await apiService.createMcpServer(payload);
      } else {
        await apiService.updateMcpServer(editing.id, payload);
      }
      setShowForm(false);
      refresh();
    } catch (err) {
      setSubmitError(err?.response?.data?.error || err?.message || 'Failed to save MCP server');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (server) => {
    if (!window.confirm(`Delete MCP server "${server.name}"? This cannot be undone.`)) return;
    try {
      await apiService.deleteMcpServer(server.id);
      refresh();
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Failed to delete MCP server');
    }
  };

  const onTest = async (server) => {
    setTestResults((prev) => ({ ...prev, [server.id]: { state: 'running' } }));
    try {
      const result = await apiService.testMcpServer(server.id);
      setTestResults((prev) => ({ ...prev, [server.id]: { state: 'done', result } }));
    } catch (err) {
      setTestResults((prev) => ({
        ...prev,
        [server.id]: {
          state: 'done',
          result: {
            success: false,
            error: err?.response?.data?.error || err?.message || 'Network error',
          },
        },
      }));
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h1>MCP Servers</h1>
          <p className="admin-page-subtitle">
            Registry of Model Context Protocol servers reachable from Python
            process-map nodes via the bundled <code>cm_mcp</code> helper.
          </p>
        </div>
        <button className="admin-primary-btn" onClick={openCreate}>
          + New MCP Server
        </button>
      </div>

      {error && <div className="admin-error-banner">{error}</div>}
      {loading ? (
        <div className="admin-loading">Loading servers…</div>
      ) : servers.length === 0 ? (
        <div className="admin-empty">
          No MCP servers configured yet. Add one so Python nodes can call its tools.
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Scope</th>
              <th>Transport</th>
              <th>Target</th>
              <th>Auth</th>
              <th>Enabled</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {servers.map((s) => {
              const test = testResults[s.id];
              return (
                <React.Fragment key={s.id}>
                  <tr>
                    <td><code>{s.name}</code>{s.description ? <div className="admin-table-sub">{s.description}</div> : null}</td>
                    <td>{s.workspace_id ? 'workspace' : 'global'}</td>
                    <td>{s.transport}</td>
                    <td>
                      {s.transport === 'stdio'
                        ? <code>{s.command || '—'}</code>
                        : <code>{s.url || '—'}</code>}
                    </td>
                    <td>{s.auth_credential_ref ? <code>{s.auth_credential_ref}</code> : '—'}</td>
                    <td>{s.enabled ? 'Yes' : 'No'}</td>
                    <td className="admin-table-actions">
                      <button className="admin-link-btn" onClick={() => onTest(s)}>Test</button>
                      <button className="admin-link-btn" onClick={() => openEdit(s)}>Edit</button>
                      <button className="admin-link-btn danger" onClick={() => onDelete(s)}>Delete</button>
                    </td>
                  </tr>
                  {test && (
                    <tr className={`admin-test-row ${test.state === 'done' ? (test.result?.success ? 'ok' : 'fail') : 'running'}`}>
                      <td colSpan={7}>
                        {test.state === 'running' && 'Testing…'}
                        {test.state === 'done' && test.result?.success && (
                          <>
                            ✓ Connected in {test.result.latency_ms}ms (HTTP {test.result.status_code})
                          </>
                        )}
                        {test.state === 'done' && !test.result?.success && (
                          <>
                            ✗ {test.result?.error || 'Connection failed'}
                            {test.result?.response_preview && (
                              <pre className="admin-test-preview">{test.result.response_preview}</pre>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="admin-modal-backdrop" onClick={() => !submitting && setShowForm(false)}>
          <div className="admin-modal admin-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? `Edit ${editing.name}` : 'New MCP Server'}</h2>
            <form onSubmit={onSubmit}>
              <div className="admin-form-row">
                <label>Name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. github, slack, filesystem"
                />
              </div>

              <div className="admin-form-row">
                <label>Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="admin-form-row">
                <label>Transport</label>
                <select
                  value={form.transport}
                  onChange={(e) => setForm({ ...form, transport: e.target.value })}
                >
                  {TRANSPORTS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              {form.transport !== 'stdio' && (
                <div className="admin-form-row">
                  <label>URL</label>
                  <input
                    type="text"
                    required
                    value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    placeholder="https://mcp.example.com/messages"
                  />
                </div>
              )}

              {form.transport === 'stdio' && (
                <>
                  <div className="admin-form-row">
                    <label>Command</label>
                    <input
                      type="text"
                      required
                      value={form.command}
                      onChange={(e) => setForm({ ...form, command: e.target.value })}
                      placeholder="e.g. /usr/local/bin/mcp-server-filesystem"
                    />
                  </div>
                  <div className="admin-form-row">
                    <label>Args (one per line)</label>
                    <textarea
                      rows={3}
                      value={form.args}
                      onChange={(e) => setForm({ ...form, args: e.target.value })}
                      placeholder="--root&#10;/workspace"
                    />
                  </div>
                  <div className="admin-form-row">
                    <label>Environment (KEY=value per line)</label>
                    <textarea
                      rows={3}
                      value={form.env}
                      onChange={(e) => setForm({ ...form, env: e.target.value })}
                      placeholder="LOG_LEVEL=info"
                    />
                  </div>
                </>
              )}

              <div className="admin-form-row">
                <label>Auth Credential (Workspace Secret)</label>
                <select
                  value={form.auth_credential_ref}
                  onChange={(e) => setForm({ ...form, auth_credential_ref: e.target.value })}
                >
                  <option value="">-- None --</option>
                  {secrets.map((s) => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>

              {form.auth_credential_ref && (
                <div className="admin-form-row">
                  <label>Auth Header Name</label>
                  <input
                    type="text"
                    value={form.auth_header_name}
                    onChange={(e) => setForm({ ...form, auth_header_name: e.target.value })}
                    placeholder="Authorization (default)"
                  />
                  <span className="admin-form-hint">
                    The plaintext secret is placed in this header. Defaults to <code>Authorization</code>.
                  </span>
                </div>
              )}

              <div className="admin-form-row">
                <label>
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  /> Enabled
                </label>
              </div>

              {!editing && (
                <div className="admin-form-row">
                  <label>Scope</label>
                  <select
                    value={form.scope}
                    onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  >
                    <option value="workspace">Workspace-scoped</option>
                    <option value="global">Global (all workspaces)</option>
                  </select>
                </div>
              )}

              {submitError && <div className="admin-error-banner">{submitError}</div>}

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  disabled={submitting}
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="admin-primary-btn" disabled={submitting}>
                  {submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Create Server')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default McpServersPage;
